import { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard,
    Package,
    Sparkles,
    Library,
    History,
    Zap,
    Settings,
    LogOut,
    Shield,
    Users,
    Home,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import './MainLayout.css';

interface MainLayoutProps {
    children: ReactNode;
}

export function MainLayout({ children }: MainLayoutProps) {
    const { userProfile, isAdmin, signOut } = useAuth();
    const navigate = useNavigate();

    const handleSignOut = async () => {
        await signOut();
        navigate('/login');
    };

    // Updated routes to use /apps/image-generator prefix
    const navItems = [
        { to: '/apps/image-generator', icon: LayoutDashboard, label: 'Dashboard', end: true },
        { to: '/apps/image-generator/products', icon: Package, label: 'Produits' },
        { to: '/apps/image-generator/generator', icon: Sparkles, label: 'Générer' },
        { to: '/apps/image-generator/prompts', icon: Library, label: 'Prompts' },
        { to: '/apps/image-generator/history', icon: History, label: 'Historique' },
        { to: '/apps/image-generator/automations', icon: Zap, label: 'Automatisations' },
    ];

    const adminItems = [
        { to: '/admin/users', icon: Users, label: 'Utilisateurs' },
        { to: '/admin/settings', icon: Settings, label: 'Paramètres' },
    ];

    return (
        <div className="main-layout">
            <aside className="sidebar glass">
                <div className="sidebar-header">
                    <div className="sidebar-logo">
                        <Sparkles size={24} />
                    </div>
                    <div className="sidebar-brand-wrapper">
                        <span className="sidebar-brand">AI Image Gen</span>
                        <span className="sidebar-company">by Duc Factory</span>
                    </div>
                </div>

                <nav className="sidebar-nav">
                    {/* Back to Portal Button */}
                    <div className="portal-back">
                        <NavLink to="/" className="portal-back-link">
                            <Home size={18} />
                            <span>Retour au Portail</span>
                        </NavLink>
                    </div>

                    <ul className="nav-list">
                        {navItems.map((item) => (
                            <li key={item.to}>
                                <NavLink
                                    to={item.to}
                                    end={item.end}
                                    className={({ isActive }) =>
                                        `nav-link ${isActive ? 'nav-link-active' : ''}`
                                    }
                                >
                                    <item.icon size={20} />
                                    <span>{item.label}</span>
                                </NavLink>
                            </li>
                        ))}
                    </ul>

                    {isAdmin && (
                        <>
                            <div className="nav-divider">
                                <Shield size={14} />
                                <span>Administration</span>
                            </div>
                            <ul className="nav-list">
                                {adminItems.map((item) => (
                                    <li key={item.to}>
                                        <NavLink
                                            to={item.to}
                                            className={({ isActive }) =>
                                                `nav-link ${isActive ? 'nav-link-active' : ''}`
                                            }
                                        >
                                            <item.icon size={20} />
                                            <span>{item.label}</span>
                                        </NavLink>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </nav>

                <div className="sidebar-footer">
                    <div className="user-info">
                        <div className="user-avatar">
                            {userProfile?.displayName?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div className="user-details">
                            <span className="user-name">{userProfile?.displayName}</span>
                            <span className="user-role">
                                {isAdmin ? 'Administrateur' : 'Utilisateur'}
                            </span>
                        </div>
                    </div>
                    <button className="logout-button" onClick={handleSignOut}>
                        <LogOut size={18} />
                    </button>
                </div>
            </aside>

            <main className="main-content">{children}</main>
        </div>
    );
}
